import { useState, useRef, useCallback, useEffect } from 'react';

interface ContentEditorProps {
  content: string;
  onChange: (content: string) => void;
  onAnalyze: () => void;
  isAnalyzing: boolean;
}

export function ContentEditor({ content, onChange, onAnalyze, isAnalyzing }: ContentEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const words = content.trim().split(/\s+/).filter(word => word.length > 0).length;
    const chars = content.length;
    setWordCount(words);
    setCharCount(chars);
  }, [content]);

  const handleContentChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = event.target.value;
    onChange(newContent);
  }, [onChange]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      const textarea = event.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = content.substring(0, start) + '  ' + content.substring(end);
      onChange(newValue);
      
      // Set cursor position after tab
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  }, [content, onChange]);



  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Content Editor</h3>
            <p className="text-sm text-gray-500">Paste or type your content for AI optimization analysis</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {content.trim().length > 0 && content.trim().length < 20 && (
              <span className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
                Need at least 20 characters to analyze
              </span>
            )}
            <button
              onClick={onAnalyze}
              disabled={isAnalyzing || content.trim().length < 20}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              {isAnalyzing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Analyzing Content...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <span>Analyze Content</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          className="w-full h-96 p-6 border-0 resize-none focus:outline-none focus:ring-0 text-gray-900 placeholder-gray-400"
          placeholder="Paste your content here for intelligent AI optimization analysis...

I'll provide specific, line-by-line recommendations to make your content more discoverable by AI assistants like Claude, GPT, and Gemini."
          spellCheck={false}
        />

      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-4">
            <span className={`${wordCount > 20 ? 'text-green-600' : 'text-gray-500'}`}>
              {wordCount} words
            </span>
            <span className="text-gray-500">{charCount} characters</span>
            {wordCount >= 20 && (
              <span className="text-green-600 text-sm flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Ready to analyze</span>
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <span>AI Analysis:</span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              isAnalyzing 
                ? 'bg-yellow-100 text-yellow-800' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {isAnalyzing ? 'Analyzing...' : 'Ready'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}